//@ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import Svg from "../../Svg/Svg";
import OccupiedField from "../OccupiedField/OccupiedField";
import {
	FieldWrapper,
	FieldActionWrapper,
	Modal,
	DialogContainer,
	ModalTitleContainer,
	ModalTitle,
	ModelSubHeading,
	ModalFooter,
	Button,
} from "@factorearth/component-library";
import styles from "./GeoSpatialField.module.scss";
import { FiMapPin, FiX } from "react-icons/fi";

import { GeoLocationService } from "services/location/geoLocationService";
import updateSingleFieldInstance from "utils/_updateSingleFieldInstance";
import { createFieldForBatchResolver } from "db/queries/fieldInstanceQueries";
import { createRevisionForBatchResolver, putRevisions } from "db/queries/revisionQueries";
import { EmbeddedTextInput } from "./EmbeddedTextField/EmbeddedTextField";
import { utmFromLatLon } from "utils/utmFromLatLon";

// If available in your repo, this is the simplest correct approach.
// If proj4 is NOT installed, see note below and stub utmFromLatLon.
// npm i proj4
// import proj4 from "proj4";

/**
 * --- UTM value contract (utm-v1) ---
 * fieldInstance.value:
 * "" OR JSON string:
 * {
 *   "easting": string,
 *   "northing": string,
 *   "zone": string,                // zone number only, "" if unknown
 *   "hemisphere": "N"|"S"|"" ,      // stored when known, otherwise ""
 *   "datum": "WGS84"|"" ,
 *   "source": "device"|"manual"|"" ,
 *   "wgs84": { "lat": number, "lon": number, "accuracy": number } // optional, device-only
 * }
 */

type UTMValue = {
	easting: string;
	northing: string;
	zone: string; // zone number only
	hemisphere: "N" | "S" | "";
	datum: "WGS84" | "";
	source: "device" | "manual" | "";
	wgs84?: { lat: number; lon: number; accuracy: number };
};

const EMPTY_UTM: UTMValue = {
	easting: "",
	northing: "",
	zone: "",
	hemisphere: "",
	datum: "",
	source: "",
};

function safeParseUTM(raw?: string | null): UTMValue {
	if (!raw || !raw.trim()) return { ...EMPTY_UTM };
	try {
		const obj = JSON.parse(raw);
		if (!obj || typeof obj !== "object") return { ...EMPTY_UTM };

		return {
			easting: typeof obj.easting === "string" ? obj.easting : (obj.easting ?? "") + "",
			northing: typeof obj.northing === "string" ? obj.northing : (obj.northing ?? "") + "",
			zone: typeof obj.zone === "string" ? obj.zone : (obj.zone ?? "") + "",
			hemisphere: obj.hemisphere === "N" || obj.hemisphere === "S" ? obj.hemisphere : "",
			datum: obj.datum === "WGS84" ? "WGS84" : "",
			source: obj.source === "device" || obj.source === "manual" ? obj.source : "",
			wgs84:
				obj.wgs84 && typeof obj.wgs84 === "object"
					? {
							lat: Number(obj.wgs84.lat),
							lon: Number(obj.wgs84.lon),
							accuracy: Number(obj.wgs84.accuracy),
						}
					: undefined,
		};
	} catch {
		return { ...EMPTY_UTM };
	}
}

function stringifyUTM(v: UTMValue): string {
	// Keep keys stable for export
	const payload: any = {
		easting: v.easting ?? "",
		northing: v.northing ?? "",
		zone: v.zone ?? "",
		hemisphere: v.hemisphere ?? "",
		datum: v.datum ?? "",
		source: v.source ?? "",
	};

	if (v.wgs84 && Number.isFinite(v.wgs84.lat) && Number.isFinite(v.wgs84.lon)) {
		payload.wgs84 = {
			lat: v.wgs84.lat,
			lon: v.wgs84.lon,
			accuracy: Number.isFinite(v.wgs84.accuracy) ? v.wgs84.accuracy : 0,
		};
	}

	return JSON.stringify(payload);
}

function formatMeters(n: number, decimals = 2): string {
	return Number.isFinite(n) ? String(Number(n.toFixed(decimals))) : "";
}

// function utmFromLatLon(lat: number, lon: number) {
//     // This computes UTM on WGS84 (correct for PWA geolocation).
//     // If proj4 isn’t available, stub this and rely on manual entry.

//     const zoneNumber = Math.floor((lon + 180) / 6) + 1; // 1..60
//     const hemisphere: "N" | "S" = lat >= 0 ? "N" : "S";
//     const utmProj = `+proj=utm +zone=${zoneNumber} +datum=WGS84 +units=m +no_defs${
//         hemisphere === "S" ? " +south" : ""
//     }`;

//     // proj4 expects [lon, lat] for EPSG:4326
//     const [easting, northing] = proj4("EPSG:4326", utmProj, [lon, lat]) as [number, number];

//     return { zoneNumber, hemisphere, easting, northing };
// }

export default function GeoSpatialFieldUTM({
	fieldInstance,
	fieldTemplate,
	color,
	online,
	userDoc,
	setupDrawer,
	fromTable,
	username,
	authUsername,
	moreHorizon,
}: {
	fieldInstance: any;
	fieldTemplate: any;
	color: any;
	online: boolean;
	userDoc: any;
	setupDrawer: any;
	fromTable: boolean;
	username: string;
	authUsername: string;
	moreHorizon: JSX.Element;
}) {
	// Template-controlled zone rendering
	// const showZone = Boolean(fieldTemplate?.options?.showZone);
	const showZone = true;
	// Parse stored value once per change
	const parsed = useMemo(() => safeParseUTM(fieldInstance.value), [fieldInstance.value]);

	// Local input state (freeform strings; no validation)
	const [eastingText, setEastingText] = useState(parsed.easting ?? "");
	const [northingText, setNorthingText] = useState(parsed.northing ?? "");
	const [zoneText, setZoneText] = useState(parsed.zone ?? "");

	const [message, setMessage] = useState<string | null>(null);
	const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

	const [gpsResults, setGpsResults] = useState<{
		latitude: number;
		longitude: number;
		accuracy: number;
		easting: string;
		northing: string;
		zone: string;
		hemisphere: "N" | "S";
	} | null>(null);

	// Keep local state in sync if fieldInstance updates from outside (collab/offline sync)
	useEffect(() => {
		const p = safeParseUTM(fieldInstance.value);
		setEastingText(p.easting ?? "");
		setNorthingText(p.northing ?? "");
		setZoneText(p.zone ?? "");
	}, [fieldInstance.value]);

	async function commitUTM(nextPartial: Partial<UTMValue>, metaOverride?: Partial<UTMValue>) {
		// Preserve existing metadata by default; only overwrite on device fill or explicit override.
		const existing = safeParseUTM(fieldInstance.value);

		const next: UTMValue = {
			...existing,
			...nextPartial,
			...metaOverride,
			easting: nextPartial.easting ?? existing.easting ?? "",
			northing: nextPartial.northing ?? existing.northing ?? "",
			zone: nextPartial.zone ?? existing.zone ?? "",
			hemisphere: (metaOverride?.hemisphere ?? existing.hemisphere ?? "") as any,
			datum: (metaOverride?.datum ?? existing.datum ?? "") as any,
			source: (metaOverride?.source ?? existing.source ?? "") as any,
			wgs84: metaOverride?.wgs84 ?? existing.wgs84,
		};

		const newValue = stringifyUTM(next);

		// If unchanged, do nothing
		if ((fieldInstance.value ?? "") === newValue) return;

		const now = new Date().toISOString();

		const updated = createFieldForBatchResolver({
			...fieldInstance,
			value: newValue,
			lastAuthor: userDoc.username,
			editing: "",
			updatedAt: now,
		});

		const revision = createRevisionForBatchResolver({
			fieldInstance: updated.id,
			value: updated.value,
			group: updated.group,
			formGroup: updated.formGroup,
			author: userDoc.username,
		});

		putRevisions([revision], online);
		await updateSingleFieldInstance(updated);
	}

	async function commitManual() {
		// No validation. Save exactly what user typed.
		// If zone is hidden, preserve existing zoneText state (which mirrors stored value),
		// but do not change it based on UI.
		await commitUTM(
			{
				easting: eastingText ?? "",
				northing: northingText ?? "",
				zone: showZone ? zoneText ?? "" : safeParseUTM(fieldInstance.value).zone ?? zoneText ?? "",
			},
			{
				// For manual entry, we set source=manual only if it was blank.
				// This avoids wiping device metadata if a user tweaks values after a GPS fill.
				source: safeParseUTM(fieldInstance.value).source ? safeParseUTM(fieldInstance.value).source : "manual",
			},
		);
	}

	async function handleMapPinClick() {
		setMessage(null);

		const result = await GeoLocationService.getCurrentPosition();

		// For Laptop testing -
		// const result = {
		//     status: "success",
		//     latitude: 37.7749,
		//     longitude: -122.4194,
		//     accuracy: 5,
		// };

		if (result.status !== "success") {
			console.log("Error getting location data:", result);
			setMessage(result.errorMessage || "Unable to get location.");
			return;
		}

		const { latitude, longitude, accuracy } = result;

		try {
			const { zoneNumber, hemisphere, easting, northing } = utmFromLatLon(latitude, longitude);

			const computed = {
				latitude,
				longitude,
				accuracy: accuracy ?? 0,
				easting: formatMeters(easting, 2),
				northing: formatMeters(northing, 2),
				zone: String(zoneNumber), // zone number only, as decided
				hemisphere,
			};

			setGpsResults(computed);
			setConfirmationModalOpen(true);
		} catch (err) {
			console.log("UTM conversion failed:", err);
			setMessage("Unable to convert device location to UTM (proj4 missing or conversion error).");
		}
	}

	async function confirmUseCurrentLocation() {
		setConfirmationModalOpen(false);
		if (!gpsResults) return;

		// Update UI immediately
		setEastingText(gpsResults.easting);
		setNorthingText(gpsResults.northing);

		// Always store zone even if not shown
		setZoneText(gpsResults.zone);

		// Commit composite value with device metadata
		await commitUTM(
			{
				easting: gpsResults.easting,
				northing: gpsResults.northing,
				zone: gpsResults.zone,
			},
			{
				hemisphere: gpsResults.hemisphere,
				datum: "WGS84",
				source: "device",
				wgs84: {
					lat: gpsResults.latitude,
					lon: gpsResults.longitude,
					accuracy: gpsResults.accuracy ?? 0,
				},
			},
		);
	}

	function handleKeyDownCapture(e: React.KeyboardEvent) {
		// Keep "/" shortcut behavior like v1: cycle focus through inputs
		if (e.key === "/") {
			const root = e.currentTarget as HTMLElement;
			const inputs = root.querySelectorAll("input,textarea,[contenteditable='true']");
			if (document.activeElement === inputs?.[0]) {
				e.preventDefault();
				(inputs?.[1] as HTMLElement | undefined)?.focus?.();
			} else if (document.activeElement === inputs?.[1] && inputs?.[2]) {
				e.preventDefault();
				(inputs?.[2] as HTMLElement | undefined)?.focus?.();
			}
		}

		// Enter commits whatever is typed (no validation)
		if (e.key === "Enter") {
			const el = document.activeElement as HTMLElement | null;
			if (el?.tagName === "INPUT") {
				commitManual();
			}
		}
	}

	return (
		<FieldWrapper
			colorPalette={color}
			label={!fromTable ? fieldTemplate.header : undefined}
			link={fieldInstance.altInstanceId ? <Svg icon="LINKED" /> : undefined}
			data-test={"Field-Header"}>
			{username && username !== authUsername ? (
				<OccupiedField username={username} moreHorizon={moreHorizon} />
			) : (
				<FieldActionWrapper>
					<div className={styles.coordFieldRow}>
						<div
							className={`${styles.coordInputShell} ${moreHorizon ? styles.withMoreHorizon : ""}`}
							data-fieldtemplateid={fieldTemplate.id}
							style={{
								color: color.text.primary,
								backgroundColor: color.background.primary,
								borderColor: color.border.primary,
							}}
							role="group"
							aria-label={`UTM - ${fieldTemplate.header}`}
							onKeyDownCapture={handleKeyDownCapture}>
							{/* Easting */}
							<EmbeddedTextInput
								colorPalette={color}
								value={eastingText}
								placeholder="easting"
								inputMode="text"
								onChange={(e) => setEastingText(e.target.value)}
								onBlur={commitManual}
							/>

							<span className={styles.coordSep} aria-hidden="true">
								/
							</span>

							{/* Northing */}
							<EmbeddedTextInput
								colorPalette={color}
								value={northingText}
								placeholder="northing"
								inputMode="text"
								onChange={(e) => setNorthingText(e.target.value)}
								onBlur={commitManual}
							/>

							{/* Zone (optional) */}
							{showZone && (
								<>
									<span className={styles.coordSep} aria-hidden="true">
										/
									</span>

									<EmbeddedTextInput
										colorPalette={color}
										value={zoneText}
										placeholder="zone"
										inputMode="text"
										onChange={(e) => setZoneText(e.target.value)}
										onBlur={commitManual}
									/>
								</>
							)}

							<button type="button" className={styles.geoIconButton} onClick={handleMapPinClick} aria-label="Use current location">
								<FiMapPin size={16} />
							</button>

							{moreHorizon && <div className={styles.coordMoreHorizon}>{moreHorizon}</div>}
						</div>

						{message && <div className={styles.message}>{message}</div>}
					</div>
				</FieldActionWrapper>
			)}

			<UpdateUTMConfirmationModal
				color={color}
				confirmationModalOpen={confirmationModalOpen}
				setConfirmationModalOpen={setConfirmationModalOpen}
				gpsResults={gpsResults}
				onConfirm={confirmUseCurrentLocation}
				showZone={showZone}
			/>
		</FieldWrapper>
	);
}

function UpdateUTMConfirmationModal({
	color,
	confirmationModalOpen,
	setConfirmationModalOpen,
	gpsResults,
	onConfirm,
	showZone,
}: {
	color: any;
	confirmationModalOpen: boolean;
	setConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	gpsResults: any;
	onConfirm: () => Promise<void>;
	showZone: boolean;
}) {
	function handleCancel() {
		setConfirmationModalOpen(false);
	}

	return (
		<Modal open={confirmationModalOpen} colorPalette={color} setOpen={setConfirmationModalOpen}>
			<DialogContainer>
				<ModalTitleContainer>
					<ModalTitle>Update UTM</ModalTitle>
					<FiX onClick={handleCancel} size={24} />
				</ModalTitleContainer>

				<ModelSubHeading>
					{gpsResults ? (
						<>
							This will update UTM to{" "}
							<b>
								{gpsResults.easting}, {gpsResults.northing}
							</b>
							{showZone ? (
								<>
									{" "}
									(Zone <b>{gpsResults.zone}</b>)
								</>
							) : (
								<> (Zone stored internally)</>
							)}
							{gpsResults.accuracy ? ` (accuracy ~ ${gpsResults.accuracy})` : ""}
						</>
					) : (
						<>No GPS results.</>
					)}
				</ModelSubHeading>

				<ModalFooter>
					<Button colorPalette={color} variant="filled" label="Confirm" onClick={onConfirm} />
					<Button colorPalette={color} variant="destructive" label="Cancel" onClick={handleCancel} />
				</ModalFooter>
			</DialogContainer>
		</Modal>
	);
}
